import { Switch } from '@material-ui/core'
import { createTheme, ThemeProvider } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { deleteBatch, getEnrolment, showRecordings, updateBatch } from 'api'
import BaseEntity from 'app/components/BaseEntity'
import Button from 'app/components/generic/Button'
import ButtonGroup from 'app/components/generic/ButtonGroup'
import ModalForm from 'app/components/ModalForm'
import {
  batchTypesMap,
  enrolmentTypes,
  indianStatesJson,
  tableColumnDefaultOption
} from 'app/config'
import constants from 'app/constants'
import {
  showToast,
  confirmDialog,
  eventEmitter,
  eventAlert,
  formatCadence
} from 'app/helpers'
import { useAccess } from 'app/hooks/useAccess'
import { fetchBatchData, updateClassData } from 'app/store/actions/classEntity'
import { batchDataTransformer } from 'app/transformer'

import { BatchActivitiesWidget } from './BatchActivitiesWidget'
import { BatchTimingsWidget } from './BatchTimingsWidget'
import { columns } from './config'
import { EnrolmentsTable } from './EnrolmentsTable'
import CreatePreschoolBatchForm from './forms/CreatePreschoolBatchForm'
import MasterForm from './MasterForm'
import { RecordingsTable } from './RecordingsTable'
import RemapStudentsModal from './RemapStudentsModal'

const theme = createTheme({
  overrides: {
    Dialog: {
      backgroundColor: 'red'
    }
  }
})

const Batch = () => {
  const batchData = batchDataTransformer(
    useSelector((state) => state.classEntityReducer.batchData)
  )
  const [modalOpen, setModalOpen] = React.useState(false)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [enrolmentsModalOpen, setEnrolmentsModalOpen] = React.useState(false)
  // Teacher remap — moves children off this batch onto another teacher's
  // batch. Preschool only; the modal owns its own preview and confirmation.
  const [remapModalOpen, setRemapModalOpen] = React.useState(false)
  const [remapBatch, setRemapBatch] = React.useState(null)
  const [preschoolModalOpen, setPreschoolModalOpen] = React.useState(false)
  const [errors, setErrors] = React.useState('')
  const [action, setAction] = React.useState('')
  const [tableData, setTableData] = React.useState('')
  const [enrolmentsData, setEnrolmentsData] = React.useState('')
  const [handler, setHandler] = React.useState(() => {})
  const [editIndex, setEditIndex] = React.useState(null)
  const dispatch = useDispatch()
  // Changing a batch's course is Super-Admin-only. This is a UI gate (see
  // permissions.js — it hides UI, it is NOT an authorization boundary); the
  // batch PATCH API stays callable. Non-super-admins keep the read-only course
  // field they already have today.
  const { isSuperAdmin } = useAccess()

  const statusList = ['active', 'upcoming']

  const openModal = React.useCallback((e, action, dataIndex) => {
    // e.preventDefault()
    setIsModalOpen(true)
    setAction(action)
    setEditIndex(dataIndex)
  }, [])

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const onActionButtonClick = async (e, action, dataIndex) => {
    openModal(e, action, dataIndex)
  }

  const onSaveClick = async (newData, action) => {
    try {
      handler({ reason: newData.reason })

      eventEmitter.emit(constants.CLOSE_MODAL_FORM)
      closeModal()
      const queries = JSON.parse(localStorage.getItem('filterQueries'))
      dispatch(fetchBatchData({ ...queries }, false, false, constants.PARENT))
    } catch (error) {
      console.log(error)
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR, error)
      eventAlert(error.message, constants.ERROR)
    }
  }

  const handleShowRecordings = React.useCallback(
    (dataIndex) => {
      const _data = batchData.data[dataIndex]
      showRecordings(_data)
        .then((data) => {
          if (data.results) {
            setTableData(
              data.results.map((schedule) => ({
                date: schedule.date,
                recordingUrl: schedule.recording_url
              }))
            )
            setModalOpen(true)
          } else {
            showToast('Oops, Unable to fetch data', constants.ERROR)
          }
        })
        .catch((e) => {
          dispatch(fetchBatchData({}, constants.BATCH))
          setErrors(e.message ? e.message : 'Unable to fetch recordings')
          showToast(
            e.message ? e.message : 'Unable to fetch recordings',
            constants.ERROR
          )
        })
    },
    [batchData, showRecordings, dispatch]
  )

  const getEnrolmentDetails = React.useCallback(
    (dataIndex) => {
      const data = batchData.data[dataIndex]
      getEnrolment({ batchId: data.uuid })
        .then((data) => {
          if (!isEmpty(data)) {
            setEnrolmentsData(
              data?.enrolment?.map((enrolment) => {
                if (
                  statusList.includes(enrolment?.status) &&
                  enrolment?.type !== enrolmentTypes.MAKE_UP_SESSION
                ) {
                  return {
                    name: enrolment?.student?.user?.full_name,
                    standard: enrolment?.student?.standard?.name,
                    start_date: dayjs(enrolment?.start_date).format(
                      'DD MMM YY'
                    ),
                    end_date: dayjs(enrolment?.end_date).format('DD MMM YY'),
                    status: enrolment?.status,
                    parent_name: enrolment?.student?.parent
                      ? `${enrolment?.student?.parent?.user?.first_name} ${enrolment?.student?.parent?.user?.last_name}`
                      : null,
                    parent_number: enrolment?.student?.parent
                      ? enrolment?.student?.parent?.user?.phone_number
                      : null
                  }
                }
              })
            )
            setEnrolmentsModalOpen(true)
          } else {
            showToast('Oops, Unable to fetch data', constants.ERROR)
          }
        })
        .catch((e) => {
          setErrors(e.message ? e.message : 'Unable to fetch enrolments')
          showToast(
            e.message ? e.message : 'Unable to fetch enrolments',
            constants.ERROR
          )
        })
    },
    [batchData]
  )

  const onChangePublish = (batchData, enabled) => {
    dispatch(
      updateClassData(constants.BATCH, constants.UPDATED, {
        uuid: batchData.uuid,
        publish: enabled
      })
    )
  }

  const modifiedColumns = React.useMemo(() => {
    // config.js hard-disables `course` (editable: false) for everyone. Re-enable
    // it only for a Super Admin; everyone else keeps it disabled as before.
    // `editable` drives the autocomplete's `disabled` prop in BaseForm.
    const gatedColumns = columns.map((column) =>
      column.name === 'course' ? { ...column, editable: isSuperAdmin } : column
    )
    // Super-Admin-only, edit-only timings editor. Only rendered for an existing
    // EXCEL batch that has a schedule_event (a cadence PATCH on a batch without
    // one 500s + rolls back — see BatchTimingsWidget). Non-super-admins never
    // get the column at all.
    const timingsEditorColumn = {
      name: 'cadence',
      label: 'Batch Timings (Super Admin)',
      widget: BatchTimingsWidget,
      disabledInFormOnCreate: true,
      options: {
        ...tableColumnDefaultOption.options,
        display: false,
        disableFilter: true
      }
    }
    return [
      ...gatedColumns,
      ...(isSuperAdmin ? [timingsEditorColumn] : []),
      {
        name: 'schedule_event.schedules',
        label: 'Batch/Schedule Timings',
        disabledInForm: true,
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'event_timings',
          disableFilter: true,
          setCellProps: () => ({
            style: {
              minWidth: '500px',
              maxWidth: '500px',
              whiteSpace: 'pre-wrap'
            }
          }),
          customBodyRenderLite: (dataIndex) => {
            const data = batchData.data[dataIndex]
            return data.type === batchTypesMap.event ||
              data.type === batchTypesMap.explore ||
              data.type === batchTypesMap.exploration ? (
              <span>
                {Array.isArray(data?.schedule_event?.schedules) &&
                  data?.schedule_event?.schedules
                    .map((item) => {
                      const start = item.start_time.split('-').join(':')
                      const end = item.end_time.split('-').join(':')
                      return `${dayjs(item.date).format('ddd DD MMM')}, ${dayjs(
                        '1/1/1 ' + start
                      ).format('h:mma')} - ${dayjs('1/1/1 ' + end).format(
                        'h:mma'
                      )}`
                    })
                    .join(' | ')}
              </span>
            ) : (
              <span>
                {Array.isArray(data.cadence) && formatCadence(data.cadence)}
              </span>
            )
          }
        }
      },
      // {
      //   name: 'publish',
      //   label: 'Publish to app',
      //   disabledInForm: true,
      //   options: {
      //     disableFilter: true,
      //     customBodyRenderLite: (dataIndex) => {
      //       const data = batchData.data[dataIndex]
      //       return (
      //         <Switch
      //           key={data.uuid}
      //           checked={data.publish}
      //           color="primary"
      //           onChange={(event) =>
      //             onChangePublish(data, event.target.checked)
      //           }
      //         />
      //       )
      //     }
      //   }
      // },
      {
        name: '',
        isAction: true,
        disabledInForm: true,
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const data = batchData.data[dataIndex]
            return (
              <ButtonGroup>
                {data?.batch_mode === 'oh_meets' ? (
                  <Button onClick={() => handleShowRecordings(dataIndex)}>
                    Show Recordings
                  </Button>
                ) : null}
              </ButtonGroup>
            )
          }
        }
      },
      {
        name: '',
        disabledInForm: true,
        options: {
          customBodyRenderLite: (dataIndex) => {
            const data = batchData.data[dataIndex]

            // Remap is preschool-only, super-admin-only, and needs both a
            // teacher to move away from and children to move. Unlike the other
            // Super-Admin gates on this page, the endpoints behind this one
            // enforce the same check server side — hiding the button is a
            // convenience, not the boundary.
            const canRemap =
              isSuperAdmin &&
              ['school', 'care'].includes(data?.type) &&
              data?.enrolment_count > 0 &&
              Boolean(data?.teacher_name)

            return data?.enrolment_count > 0 ? (
              <ButtonGroup>
                <Button onClick={() => getEnrolmentDetails(dataIndex)}>
                  See enrolments
                </Button>
                {canRemap ? (
                  <Button
                    onClick={() => {
                      setRemapBatch(data)
                      setRemapModalOpen(true)
                    }}
                  >
                    Remap student
                  </Button>
                ) : null}
              </ButtonGroup>
            ) : null
          }
        }
      }
    ]
  }, [batchData, isSuperAdmin])

  const customHandleDelete = async (
    handlerFunction = () => {},
    rowsDeleted
  ) => {
    try {
      const data = batchData.data[rowsDeleted?.data[0]?.dataIndex]
      if (data.enrolment_count > 0) handlerFunction()
      else {
        onActionButtonClick(null, 'delete', rowsDeleted?.data[0]?.dataIndex)

        setHandler(() => (val) => handlerFunction(val))
      }
    } catch (error) {
      console.log(error)
    }
  }

  // Refresh the list with whatever filters are currently applied, the same way
  // the delete flow does, so a freshly created batch shows up without a reload.
  const refreshBatchList = React.useCallback(() => {
    const queries = JSON.parse(localStorage.getItem('filterQueries'))
    dispatch(fetchBatchData({ ...queries }, false, false, constants.PARENT))
  }, [dispatch])

  return (
    <>
      {/* Creating a preschool batch is Super-Admin-only. Unlike the other
          Super-Admin gates on this page, the endpoint behind this one enforces
          the same check server side — it writes a year of schedules and a
          capacity row that the member app's booking flow reads. */}
      {isSuperAdmin ? (
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', padding: 8 }}
        >
          <Button onClick={() => setPreschoolModalOpen(true)}>
            Create Preschool Batch
          </Button>
        </div>
      ) : null}
      <BaseEntity
        entity={constants.BATCH}
        label={constants.BATCH_LABEL}
        columns={modifiedColumns}
        data={batchData}
        fetchData={fetchBatchData}
        updateEntityData={updateClassData}
        customHandleDelete={customHandleDelete}
        createDependency
        selectableRows="single"
      />
      <ThemeProvider theme={theme}>
        <ModalForm
          disableBackdropClick={!errors}
          containerStyles={{ borderRadius: 10 }}
          className="border"
          isModalOpen={modalOpen}
          onModalClose={() => setModalOpen(false)}
          enableSave={false}
          modalTitle="Recordings Data"
        >
          <RecordingsTable data={tableData} />
        </ModalForm>
      </ThemeProvider>
      <ThemeProvider theme={theme}>
        <ModalForm
          disableBackdropClick={!errors}
          containerStyles={{ borderRadius: 10 }}
          className="border"
          isModalOpen={enrolmentsModalOpen}
          onModalClose={() => setEnrolmentsModalOpen(false)}
          enableSave={false}
          modalTitle="Enrolments"
        >
          <EnrolmentsTable data={enrolmentsData} />
        </ModalForm>
      </ThemeProvider>
      <MasterForm
        isModalOpen={isModalOpen}
        onSaveClick={onSaveClick}
        closeModal={closeModal}
        action={action}
        data={batchData?.data[editIndex]}
      />
      {isSuperAdmin ? (
        <CreatePreschoolBatchForm
          isModalOpen={preschoolModalOpen}
          closeModal={() => setPreschoolModalOpen(false)}
          onCreated={refreshBatchList}
        />
      ) : null}
      {isSuperAdmin ? (
        <RemapStudentsModal
          isModalOpen={remapModalOpen}
          batch={remapBatch}
          onClose={() => {
            setRemapModalOpen(false)
            setRemapBatch(null)
            // Teacher, capacity and enrolment_count all move on a remap — and
            // a brand-new destination batch may now exist — so refetch the
            // list rather than patching the row in place.
            refreshBatchList()
          }}
        />
      ) : null}
    </>
  )
}

export default Batch
